import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import SiteChrome from "@/components/candidate/SiteChrome";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({
    baseURL: `${BASE}/api`,
    headers: { "Content-Type": "application/json" },
    timeout: 15000,
});

function apiError(err) {
    const d = err?.response?.data?.detail;
    if (typeof d === "string") return d;
    return err?.message || "Something went wrong.";
}

export default function CandidateDetails() {
    const location = useLocation();
    const navigate = useNavigate();
    const [candidate, setCandidate] = useState(location.state?.candidate || null);
    const token = location.state?.verification_token || "";
    const [saving, setSaving] = useState(false);
    const [colleges, setColleges] = useState([]);
    const [form, setForm] = useState({
        name: candidate?.name || "",
        college_name: candidate?.college_name || "",
        course: candidate?.course || "",
        branch: candidate?.branch || "",
    });

    useEffect(() => {
        api.get("/public/colleges")
            .then(({ data }) => setColleges(data || []))
            .catch(() => setColleges([]));
    }, []);

    if (!candidate || !token) {
        return (
            <SiteChrome eyebrow="Session Expired">
                <div className="max-w-lg mx-auto px-6 py-16 text-center">
                    <div className="font-display text-3xl mb-3">
                        Session expired
                    </div>
                    <p className="text-sm text-[#4B5563] mb-6">
                        Please verify your OTP again to see your details.
                    </p>
                    <button
                        data-testid="btn-back-to-verify"
                        onClick={() => navigate("/verify")}
                        className="px-4 py-2 text-sm bg-[#0F1F38] text-[#F7F5F0]"
                    >
                        Go to verification
                    </button>
                </div>
            </SiteChrome>
        );
    }

    const confirm = async () => {
        setSaving(true);
        try {
            const { data } = await api.post(
                `/public/candidate/${candidate.id}/confirm`,
                { verification_token: token, ...form },
            );
            setCandidate(data);
            navigate("/verify/thank-you", {
                state: { candidate: data, selfRegistered: false },
            });
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <SiteChrome eyebrow="Interview Attendance">
            <div
                data-testid="candidate-details-page"
                className="max-w-3xl mx-auto px-6 md:px-10 py-10 md:py-16"
            >
                <div className="mb-8 md:mb-10">
                    <div
                        data-testid="attendance-badge"
                        className="inline-flex items-center gap-2 px-3 py-1 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 mb-4"
                    >
                        <CheckCircle2 size={14} />
                        Attendance {candidate.attendance_status}
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl leading-tight">
                        Welcome, {candidate.name}
                    </h1>
                    <p className="text-sm text-[#4B5563] mt-2">
                        Verified on{" "}
                        <strong>{candidate.attendance_verified_date}</strong> at{" "}
                        <strong>{candidate.attendance_verified_time}</strong>.
                        Review your details below and hit <strong>Confirm</strong> to
                        finish the attendance process.
                    </p>
                </div>

                <div className="bg-white border border-[#0F1F38]/10 divide-y divide-[#0F1F38]/10">
                    <section className="p-6 md:p-8">
                        <div className="text-[10px] uppercase tracking-label text-[#0F1F38]/60 mb-4">
                            Editable details
                        </div>
                        <fieldset
                            disabled={saving}
                            className="grid grid-cols-1 md:grid-cols-2 gap-4"
                        >
                            <EditField
                                label="Name"
                                testid="edit-name"
                                value={form.name}
                                onChange={(v) => setForm({ ...form, name: v })}
                            />
                            <CollegeSelect
                                testid="edit-college"
                                value={form.college_name}
                                onChange={(v) =>
                                    setForm({ ...form, college_name: v })
                                }
                                colleges={colleges}
                            />
                            <EditField
                                label="Course"
                                testid="edit-course"
                                value={form.course}
                                onChange={(v) => setForm({ ...form, course: v })}
                            />
                            <EditField
                                label="Branch"
                                testid="edit-branch"
                                value={form.branch}
                                onChange={(v) => setForm({ ...form, branch: v })}
                            />
                        </fieldset>
                    </section>

                    <section className="p-6 md:p-8 bg-[#F7F5F0]/40">
                        <div className="text-[10px] uppercase tracking-label text-[#0F1F38]/60 mb-4">
                            Read-only information
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <ReadRow label="Phone Number" value={candidate.phone} />
                            <ReadRow label="Email" value={candidate.email} />
                            <ReadRow label="Role" value={candidate.role} />
                            <ReadRow
                                label="Schedule Date"
                                value={candidate.schedule_date}
                            />
                            <ReadRow
                                label="Schedule Time"
                                value={candidate.schedule_time}
                            />
                        </div>
                    </section>

                    <section className="p-6 md:p-8">
                        <button
                            data-testid="btn-confirm-attendance"
                            onClick={confirm}
                            disabled={saving || !form.name?.trim()}
                            className="w-full bg-[#0F1F38] text-[#F7F5F0] py-3 text-sm font-medium inline-flex items-center justify-center gap-2 hover:bg-[#1A3254] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saving ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                <CheckCircle2 size={14} />
                            )}
                            Confirm
                        </button>
                        <p className="text-xs text-center text-[#4B5563] mt-3">
                            Once confirmed, these details are locked. Contact HR for
                            any further changes.
                        </p>
                    </section>
                </div>
            </div>
        </SiteChrome>
    );
}

function EditField({ label, testid, value, onChange }) {
    return (
        <div>
            <Label className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                {label}
            </Label>
            <Input
                data-testid={testid}
                value={value}
                onChange={(e) => onChange(e.target.value)}
            />
        </div>
    );
}

function CollegeSelect({ testid, value, onChange, colleges }) {
    return (
        <div>
            <Label className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                College Name
            </Label>
            <Select value={value || ""} onValueChange={onChange}>
                <SelectTrigger data-testid={testid}>
                    <SelectValue placeholder="Select college" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                    {value && !colleges.some((c) => c.name === value) && (
                        <SelectItem value={value}>{value} (inactive)</SelectItem>
                    )}
                    {colleges.map((c) => (
                        <SelectItem key={c.name} value={c.name}>
                            {c.name}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}

function ReadRow({ label, value }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-label text-[#0F1F38]/50">
                {label}
            </div>
            <div className="mt-1 text-[#0F1F38] break-words">{value || "—"}</div>
        </div>
    );
}
